import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import NetworkApi from "../../services/networks";
import CreateNetworkForm from "../../forms/CreateNetworkForm"
import CountryApi from "../../services/countries";
import StateApi from "../../services/states";
import MunicipalityApi from "../../services/municipalities";
import Error from "../../fragments/form/Error"
import Message from "../../fragments/form/Message"

function CreateNetworkPage(props) {
    const [location, setLocation] = useState({
        address_1: '',
        address2: '',
        city: '',
        state: '',
        country: '',
        zip_code: '',
        latitude: null,
        longitude: null,
        country_id: null,
        state_id: null,
        city_id: null
    });
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const navigate = useNavigate();
    const networkApi = new NetworkApi(props.token);
    const countryApi = new CountryApi(props.token);
    const stateApi = new StateApi(props.token);
    const municipalityApi = new MunicipalityApi(props.token);

    useEffect(() => {
        loadCountries();
    }, []);

    const loadCountries = async () => {
        try {
            const response = await countryApi.all();
            setCountries(response.countries || []);
        } catch (error) {
            console.error("Error loading countries:", error);
        }
    };

    const handleSearchLocation = async (id, search) => {
        try {
            if (id === 'country') {
                const response = await countryApi.search(search);
                setCountries(response.countries || []);
            } else if (id === 'state' && location.country_id) {
                const response = await stateApi.search(location.country_id, search);
                setStates(response.states || []);
            } else if (id === 'city' && location.state_id) {
                const response = await municipalityApi.search(location.state_id, search);
                setCities(response.cities || []);
            }
        } catch (error) {
            console.error("Error searching location:", error);
        }
    };

    const handleSetLocation = (newLocation) => {
        setLocation(newLocation);
    };

    const getErrorMessage = (error) => {
        // Network errors (no response from server)
        if (!error.response) {
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                return "Network connection error. Please check your internet connection and try again.";
            }
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                return "Request timed out. The server is taking too long to respond. Please try again.";
            }
            if (error.code === 'ERR_INTERNET_DISCONNECTED') {
                return "No internet connection. Please check your network settings.";
            }
            return "Network error occurred. Please check your connection and try again.";
        }

        // API errors (server responded with error status)
        const status = error.response?.status;
        const data = error.response?.data;

        if (status === 400) {
            // Validation errors
            const message = data?.message || data?.error || "Invalid data provided. Please check your input.";
            return message;
        }
        if (status === 401) {
            return "Authentication failed. Please log in again.";
        }
        if (status === 403) {
            return "You don't have permission to create networks.";
        }
        if (status === 404) {
            return "Network endpoint not found. Please contact support.";
        }
        if (status === 409) {
            return data?.message || "A network with this subdomain or domain already exists.";
        }
        if (status === 413) {
            return "File too large. Please reduce the image size and try again.";
        }
        if (status >= 500) {
            return "Server error occurred. Please try again later or contact support.";
        }

        // Generic API error
        return data?.message || data?.error || `Error: ${status} - ${error.message}`;
    };

    const onSubmit = async (formData, locationData) => {
        try {
            // Add location data to form data
            if (locationData) {
                Object.keys(locationData).forEach(key => {
                    if (locationData[key] !== null && locationData[key] !== '') {
                        // Map address_1 to address for API compatibility
                        const apiKey = key === 'address_1' ? 'address' : key;
                        formData.set(apiKey, locationData[key]);
                    }
                });
            }

            const response = await networkApi.create(formData);
            console.log("Network creation response:", response);
            
            if (response && (response.id || response.network?.id)) {
                setMessage("Network created successfully!");
                setError(null);
                setTimeout(() => {
                    navigate('/networks');
                }, 2000);
            } else {
                setError("Failed to create network - no ID returned from server");
                setMessage(null);
            }
        } catch (error) {
            console.error("Error creating network:", error);
            const errorMessage = getErrorMessage(error);
            setError(errorMessage);
            setMessage(null);
        }
    };

    return (
        <div className='dashboard-content'>
            <div className='dashboard-content-container'>
                <div className='dashboard-content-header'>
                    <h2>Create New Network</h2>
                </div>
                <Error message={error} />
                <Message message={message} />
                <CreateNetworkForm
                    onSubmit={onSubmit}
                    location={location}
                    handleSetLocation={handleSetLocation}
                    handleSearchLocation={handleSearchLocation}
                    countries={countries}
                    states={states}
                    cities={cities}
                    {...props}
                />
            </div>
        </div>
    )
}

export default CreateNetworkPage
